"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
exports.__esModule = true;
exports.Incentive = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _sdkCore = require("@acala-network/sdk-core");
var _util = require("@polkadot/util");
var _rxjs = require("rxjs");
var _operators = require("rxjs/operators");
var _errors = require("../liquidity/errors");
var _storages = require("./storages");
var _types = require("./types");
var _getApr = require("./utils/get-apr");
var _getDeductionEndtimeConfigs = require("./utils/get-deduction-endtime-configs");
var _getPoolId = require("./utils/get-pool-id");
var _getPoolToken = require("./utils/get-pool-token");
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { (0, _defineProperty2.default)(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
class Incentive {
  constructor(_ref) {
    let {
      api,
      wallet
    } = _ref;
    this.api = void 0;
    this.storages = void 0;
    this.wallet = void 0;
    this.isReady$ = void 0;
    this.consts = void 0;
    // get `list` tokens informations from wallet sdk
    this.getTokens = list => {
      return Object.fromEntries(Array.from(new Set(list.map(_sdkCore.forceToCurrencyName))).map(item => [item, this.wallet.getToken(item)]));
    };
    // subscribe all deduction rates config
    this.deductionRates$ = (0, _util.memoize)(() => {
      return this.storages.claimRewardDeductionRates().observable.pipe((0, _operators.map)(data => {
        return Object.fromEntries(data.map(item => {
          const pool = item[0].args[0];
          const id = (0, _getPoolId.getPoolId)(pool);
          const rate = _sdkCore.FixedPointNumber.fromInner(item[1].toString());
          return [id, rate];
        }));
      }));
    });
    // subscirbe all incentive reward amounts config
    this.rewardTokensConfigs$ = (0, _util.memoize)(() => {
      return this.storages.incentiveRewardAmounts().observable.pipe((0, _operators.map)(data => {
        const allRewardTokens = data.map(item => (0, _sdkCore.forceToCurrencyName)(item[0].args[1]));
        const tokens = this.getTokens(allRewardTokens);
        const result = {};
        data.forEach(item => {
          const pool = item[0].args[0];
          const rewardCurrencyId = item[0].args[1];
          const rewardToken = tokens[(0, _sdkCore.forceToCurrencyName)(rewardCurrencyId)];
          const rewardAmount = _sdkCore.FixedPointNumber.fromInner(item[1].toString(), rewardToken.decimals);
          const id = (0, _getPoolId.getPoolId)(pool);

          // insert rewards config if poolId is exists
          if (result[id]) {
            result[id].push({
              token: rewardToken,
              amount: rewardAmount
            });
            return;
          }
          result[id] = [{
            token: rewardToken,
            amount: rewardAmount
          }];
        });
        return result;
      }));
    });
    // subscribe all pool infomations
    this.poolInfos$ = (0, _util.memoize)(() => {
      return this.storages.poolInfos().observable.pipe((0, _operators.map)(data => {
        const allRewardTokens = data.flatMap(item => Array.from(item[1].rewards.entries()).map(_ref2 => {
          let [key] = _ref2;
          return (0, _sdkCore.forceToCurrencyName)(key);
        }));
        const tokens = this.getTokens([...data.map(item => (0, _getPoolToken.getPoolToken)(item[0].args[0])), ...allRewardTokens]);
        return data.map(item => {
          const raw = item[0].args[0];
          const id = (0, _getPoolId.getPoolId)(raw);
          const type = raw.isDex ? _types.IncentiveType.DEX : raw.isLoans ? _types.IncentiveType.LOANS : _types.IncentiveType.Earning;
          const collateral = tokens[(0, _sdkCore.forceToCurrencyName)((0, _getPoolToken.getPoolToken)(raw))];
          const totalShares = _sdkCore.FixedPointNumber.fromInner(item[1].totalShares.toString(), collateral.decimals);

          // format rewards infomations
          const rewards = Array.from(item[1].rewards.entries()).map(_ref3 => {
            let [key, values] = _ref3;
            const token = tokens[(0, _sdkCore.forceToCurrencyName)(key)];
            const total = _sdkCore.FixedPointNumber.fromInner(values[0].toString(), token.decimals);
            const withdrawn = _sdkCore.FixedPointNumber.fromInner(values[1].toString(), token.decimals);
            return {
              rewardToken: token,
              totalReward: total,
              withdrawnReward: withdrawn,
              claimableReward: total.minus(withdrawn).max(new _sdkCore.FixedPointNumber(0, collateral.decimals))
            };
          });
          return {
            collateral,
            type,
            id,
            raw,
            totalShares,
            rewards
          };
        });
      }));
    });
    this.endTimes$ = (0, _util.memoize)(() => {
      return this.storages.scheduler().observable.pipe((0, _operators.map)(data => (0, _getDeductionEndtimeConfigs.getDeductionEndtimeConfigs)(this.api, data)));
    });
    this.apr$ = (0, _util.memoize)(pool => {
      const tokens = Array.from(new Set([(0, _sdkCore.forceToCurrencyName)(pool.collateral), ...pool.rewardTokensConfig.map(item => item.token.name)]));
      return (0, _rxjs.combineLatest)({
        prices: (0, _rxjs.combineLatest)(Object.fromEntries(tokens.map(item => [item, this.wallet.subscribePrice(item)])))
      }).pipe((0, _operators.map)(_ref4 => {
        let {
          prices
        } = _ref4;
        return (0, _getApr.getAPR)(pool.collateral, this.consts.accumulatePeriod, prices, pool.totalShares, pool.deductionRate, pool.rewardTokensConfig);
      }));
    });
    this.userIncentive$ = (0, _util.memoize)((poolId, address) => {
      return this.subscribeIncentivePoolById(poolId).pipe((0, _operators.switchMap)(info => {
        if (!info) throw new _errors.IncentivePoolNotFound(poolId);
        return (0, _rxjs.combineLatest)({
          info: (0, _rxjs.of)(info),
          sharesAndWithdrawn: this.storages.userSharesAndWithdrawnRewards(info.raw, address).observable,
          pendingRewards: this.storages.pendingRewards(info.raw, address).observable
        });
      }), (0, _operators.map)(_ref5 => {
        let {
          info,
          sharesAndWithdrawn,
          pendingRewards
        } = _ref5;
        const tokens = this.getTokens([...Array.from(sharesAndWithdrawn[1].entries()).map(item => {
          return (0, _sdkCore.forceToCurrencyName)(item[0]);
        }), ...Array.from(pendingRewards.entries()).map(item => {
          return (0, _sdkCore.forceToCurrencyName)(item[0]);
        })]);
        const userShares = _sdkCore.FixedPointNumber.fromInner(sharesAndWithdrawn[0].toString(), info.collateral.decimals);
        const withdrawns = Array.from(sharesAndWithdrawn[1].entries()).map(entry => {
          const tokenName = (0, _sdkCore.forceToCurrencyName)(entry[0]);
          const token = tokens[tokenName];
          const withdrawnReward = _sdkCore.FixedPointNumber.fromInner(entry[1].toString(), token.decimals);
          return {
            token,
            withdrawnReward
          };
        });
        const pendings = Array.from(pendingRewards.entries()).map(entry => {
          const tokenName = (0, _sdkCore.forceToCurrencyName)(entry[0]);
          const token = tokens[tokenName];
          const reward = _sdkCore.FixedPointNumber.fromInner(entry[1].toString(), token.decimals);
          return {
            reward,
            token
          };
        });
        const ratio = userShares.div(info.totalShares);
        const rewards = info.rewards.map(item => {
          const token = item.rewardToken;
          const pendingReward = pendings.find(pending => pending.token.isEqual(token));
          const withdrawnReward = withdrawns.find(withdrawn => withdrawn.token.isEqual(token));
          const claimableReward = ratio.mul(item.totalReward).minus((withdrawnReward === null || withdrawnReward === void 0 ? void 0 : withdrawnReward.withdrawnReward) || _sdkCore.FixedPointNumber.ZERO).add((pendingReward === null || pendingReward === void 0 ? void 0 : pendingReward.reward) || _sdkCore.FixedPointNumber.ZERO).max(_sdkCore.FixedPointNumber.ZERO);
          const claimableRewardWithDeduction = claimableReward.minus(claimableReward.mul(info.deductionRate || _sdkCore.FixedPointNumber.ZERO)).max(_sdkCore.FixedPointNumber.ZERO);
          return {
            rewardToken: token,
            withdrawnReward: withdrawnReward ? withdrawnReward.withdrawnReward : _sdkCore.FixedPointNumber.ZERO,
            claimableReward,
            claimableRewardWithDeduction
          };
        });
        return {
          shares: userShares,
          rewards,
          pool: info
        };
      }));
    });
    this.subscribeAllIncentivePools = (0, _util.memoize)(() => {
      return (0, _rxjs.combineLatest)({
        endTimes: this.endTimes$(),
        deductionRates: this.deductionRates$(),
        poolInfos: this.poolInfos$(),
        rewardTokensConfigs: this.rewardTokensConfigs$()
      }).pipe((0, _operators.map)(_ref6 => {
        let {
          endTimes,
          deductionRates,
          poolInfos,
          rewardTokensConfigs
        } = _ref6;
        return poolInfos.map(item => {
          const deductionRate = deductionRates[item.id] || _sdkCore.FixedPointNumber.ZERO;
          const rewardTokensConfig = rewardTokensConfigs[item.id] || [];
          const endBlockNumber = endTimes[item.id] || -1;
          const result = _objectSpread(_objectSpread({}, item), {}, {
            enable: rewardTokensConfig.length !== 0,
            rewardTokensConfig,
            deductionRate,
            endBlockNumber
          });
          return result;
        });
      }), (0, _operators.switchMap)(poolInfos => {
        return (0, _rxjs.combineLatest)(Object.fromEntries(poolInfos.map(item => [item.id, this.apr$(item)]))).pipe((0, _operators.map)(aprs => {
          return poolInfos.map(item => {
            return _objectSpread(_objectSpread({}, item), {}, {
              apr: aprs[item.id]
            });
          });
        }));
      }), (0, _operators.shareReplay)(1));
    });
    this.api = api;
    this.storages = (0, _storages.createStorages)(this.api);
    this.isReady$ = new _rxjs.BehaviorSubject(false);
    this.wallet = wallet;
    this.consts = {
      accumulatePeriod: this.api.consts.incentives.accumulatePeriod.toNumber()
    };
    this.init();
  }
  init() {
    // set sdk ready status until get all incentive pools information
    this.subscribeAllIncentivePools().subscribe({
      next: () => this.isReady$.next(true)
    });
  }
  get isReady() {
    return (0, _rxjs.firstValueFrom)(this.isReady$.asObservable().pipe((0, _operators.filter)(i => i)));
  }
  getAllIncentivePools() {
    return (0, _rxjs.firstValueFrom)(this.subscribeAllIncentivePools());
  }
  subscribeIncentivePoolById(id) {
    return this.subscribeAllIncentivePools().pipe((0, _operators.map)(data => {
      return data.find(item => {
        return item.id === id;
      });
    }));
  }
  getIncentivePoolById(id) {
    return (0, _rxjs.firstValueFrom)(this.subscribeIncentivePoolById(id));
  }
  subscribeIncentivePool(type, currency) {
    return this.subscribeAllIncentivePools().pipe((0, _operators.map)(data => {
      return data.find(item => {
        return item.type === type && (0, _sdkCore.forceToCurrencyName)((0, _getPoolToken.getPoolToken)(item.raw)) === (0, _sdkCore.forceToCurrencyName)(currency);
      });
    }));
  }
  getIncnetivePool(type, currency) {
    return (0, _rxjs.firstValueFrom)(this.subscribeIncentivePool(type, currency));
  }
  subscribeUserIncentive(id, address) {
    return this.userIncentive$(id, address);
  }
  getUserIncentive(id, address) {
    return (0, _rxjs.firstValueFrom)(this.subscribeUserIncentive(id, address));
  }
}
exports.Incentive = Incentive;